import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	IUpdateModifierGroupsV2,
	IGetModifierGroupInfoV2,
} from 'types';
import { v4 as uuidv4 } from 'uuid';
import {
	editModifierGroupInitialState,
	EditModifierGroupContext,
	TTypeOfModalWindow,
} from './editModifierGroupContext';
import { editModifierGroupReducer } from './editModifierGroupReducer';
import { EditModifierGroupActionTypes } from './editModifierGroupActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierGroupInfoV2 } from 'backend/modifierGroups';

export const EditModifierGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editModifierGroupReducer, editModifierGroupInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const modifierGroupID = params.get('modifierGroupID');

	const { response, loading, refetch } = useFetchData<IGetModifierGroupInfoV2, string>(
		modifierGroupID ? getModifierGroupInfoV2 : null,
		modifierGroupID || '',
	);

	useNamedHeader(response?.name);

	useEffect(() => {
		if (response) {
			const translations = response.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name || '',
			}));

			dispatch({
				type: EditModifierGroupActionTypes.SET_ALL_DATA,
				payload: {
					modifierGroupID: modifierGroupID ? modifierGroupID : '',
					initialIDSelectedCustomer: response.customer.id,
					initialIDsSelectedRestaurant: response.restaurants.map((r) => r.id),
					nameOfModifierGroup: response.name || '',
					externalId: response.externalId || '',
					articleNumber: response.articleNumber || '',
					translations,
				},
			});
		}
	}, [modifierGroupID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditModifierGroupActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierGroupActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierGroupActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfModifierGroup = useCallback((value: string) => {
		dispatch({
			type: EditModifierGroupActionTypes.CHANGE_NAME_OF_MODIFIER_GROUP,
			payload: {
				nameOfModifierGroup: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditModifierGroupActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: EditModifierGroupActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierGroupActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateModifierGroupsV2>) => {
		dispatch({
			type: EditModifierGroupActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditModifierGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditModifierGroupContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfModifierGroup,
				changeExternalId,
				generateUniqueExternalId,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				modifierGroupID: state.modifierGroupID,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfModifierGroup: state.nameOfModifierGroup,
				externalId: state.externalId,
				articleNumber: state.articleNumber,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditModifierGroupContext.Provider>
	);
};
